import { createClient } from '@supabase/supabase-js'
import { createContext, useContext } from 'react'

/**
 * A porta para o Realtime do Supabase (RN-ASS-32, T39): o app escuta inserções numa tabela da clínica
 * e reage na hora, sem recarregar. A RLS da tabela decide o que chega. Em teste e em story, a porta é
 * nula (nada escuta) ou um falso que dispara eventos à mão.
 */
export interface RealtimeClient {
  /** Escuta inserções em `table` que passem por `filter` ("clinic_id=eq.<id>"); devolve quem cancela. */
  onInsert(
    table: string,
    filter: string,
    handler: (row: Record<string, unknown>) => void,
  ): () => void
}

export function createSupabaseRealtime(
  url: string,
  anonKey: string,
  getAccessToken: () => Promise<string | null>,
): RealtimeClient {
  const client = createClient(url, anonKey, {
    auth: { persistSession: false, autoRefreshToken: false },
  })
  return {
    onInsert(table, filter, handler) {
      let stopped = false
      const channel = client.channel(`${table}:${filter}`)
      void getAccessToken().then((token) => {
        if (stopped) return
        if (token) void client.realtime.setAuth(token)
        channel
          .on('postgres_changes', { event: 'INSERT', schema: 'public', table, filter }, (payload) =>
            handler(payload.new as Record<string, unknown>),
          )
          .subscribe()
      })
      return () => {
        stopped = true
        void client.removeChannel(channel)
      }
    },
  }
}

export const RealtimeContext = createContext<RealtimeClient | null>(null)

/** O cliente do Realtime, ou `null` onde não há escuta (testes, stories). */
export function useRealtime(): RealtimeClient | null {
  return useContext(RealtimeContext)
}
